import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Switch, Tooltip } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React from 'react';
import useAsyncGuiHandler from '../../../../../hooks/useAsyncHandler';
import { Booth } from '../../../../../interfaces/interfaces';
import { fetchAndSetAdminBooth } from '../../../../../reduxToolkit/slices/adminSlice';
import { fetchAndSetBooths, setBooths } from '../../../../../reduxToolkit/slices/commonSlice';
import { useDispatch, useSelector } from '../../../../../reduxToolkit/typedRedux';
import { fileUpload, updateBooth } from '../../../../../services/services';
import { VyUploadCrop } from '../../../../common/VyUploadCrop';
import Slider from '../../../../event/SlickSliderNew';
import { VyAspectRatioHint } from '../../../../common/VyAspectRatioHint';
import { SlideEditModal } from './SlideEditModal';
import { useState } from 'react';

interface BoothHeaderProps {
  booth: Booth;
}

const BoothHeaderImages = ({ booth }: BoothHeaderProps) => {
  const booths = useSelector((state) => state.common.booths) ?? [];
  const event = useSelector((state) => state.common.event ?? state.admin.event);

  const [showEditModal, setShowEditModal] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [handleAsync] = useAsyncGuiHandler();

  async function uploadHeaderImage(blob: Blob) {
    if (!booth) return;
    const data = await fileUpload(blob);
    const oldImages = booth.headerImages || [];
    const headerImages = [...oldImages, data.data.fileId];
    const _booth = await updateBooth({ boothid: booth._id, headerImages });
    dispatch(setBooths(booths.map((b) => (b._id === _booth._id ? _booth : b))));
    refresh();
  }

  async function setAutoSlideshow(value: boolean) {
    if (!booth) return;
    const _booth = await updateBooth({ boothid: booth._id, settings: { autoSlideshow: { value } } });
    dispatch(setBooths(booths.map((b) => (b._id === _booth._id ? _booth : b))));
    refresh();
  }

  function refresh() {
    if (booth) dispatch(fetchAndSetAdminBooth({ boothid: booth?._id }));
    if (event?.slug) dispatch(fetchAndSetBooths(event?.slug));
  }

  const minHeight = booth?.headerImages && booth?.headerImages.length > 0 ? {} : { minHeight: 150 };

  return (
    <div className="grid grid-cols-1" style={minHeight}>
      <div className="object-cover z-0 col-start-1 row-start-1 w-full h-full">
        <Slider slides={booth.headerImages ?? []} autoplay={booth?.settings?.autoSlideshow?.value ?? false} />
      </div>
      <div className="z-0 col-start-1 row-start-2 px-8 py-4 sm:row-start-1 sm:mr-28">
        <VyUploadCrop onImage={(blob) => handleAsync(uploadHeaderImage(blob))} cropAspect={4} uploadMultiple>
          <div className="flex z-50 flex-col gap-1 p-4 text-white rounded-md opacity-75 cursor-pointer sm:items-center sm:flex-row bg-slate-700 hover:opacity-100">
            <FontAwesomeIcon style={{ height: '30px', width: '30px' }} className="mr-4 w-4 h-4" icon={faUpload} />
            <div>
              <b>{t('booth:upload-images')}</b>
              <p>{t('booth:slideshow-explain')}</p>
            </div>
            <VyAspectRatioHint aspectRatio="1000 x 250" />
          </div>
        </VyUploadCrop>
        {booth && booth.headerImages && booth.headerImages.length > 0 && (
          <div
            className="flex items-center p-2 mt-1 text-white rounded-md opacity-75 cursor-pointer bg-slate-700 hover:opacity-100"
            onClick={() => setShowEditModal(true)}>
            <b>{t('booth:edit-slides')}</b>
          </div>
        )}
        {showEditModal && <SlideEditModal booth={booth} onUpdate={refresh} onClose={() => setShowEditModal(false)} />}
        <Tooltip title={t('booth:auto-slideshow-explain').toString()}>
          <div className="flex justify-between items-center p-2 mt-1 text-white rounded-md opacity-75 cursor-pointer bg-slate-700 hover:opacity-100">
            <b>{t('booth:auto-slideshow')}</b>
            <Switch defaultChecked={booth?.settings?.autoSlideshow?.value ?? false} size="small" onChange={(e) => setAutoSlideshow(e.target.checked)} />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default BoothHeaderImages;
